import { BufferAttribute, BufferGeometry } from 'three';

/**
 * One indexed geometry of the parts, in their order: every vertex attribute any part has, zero at
 * the vertices of the parts without it, and each part's faces with its vertices numbered after
 * those of the parts before it. It has no bounding sphere until one is asked for: the ships drawn
 * from it are never culled, and three.js works one out for the first ray cast at them.
 */
export function joinGeometries(parts: readonly BufferGeometry[]): BufferGeometry {
  const counts = parts.map((part) => part.getAttribute('position').count);
  const firsts = counts.map((_, i) => counts.slice(0, i).reduce((sum, n) => sum + n, 0));
  const total = counts.reduce((sum, n) => sum + n, 0);
  const joined = new BufferGeometry();
  new Set(parts.flatMap((part) => Object.keys(part.attributes))).forEach((name) => {
    const size = parts.find((part) => part.hasAttribute(name))!.getAttribute(name).itemSize;
    const values = new Float32Array(total * size);
    parts.forEach((part, i) => {
      if (part.hasAttribute(name)) values.set(part.getAttribute(name).array, firsts[i]! * size);
    });
    joined.setAttribute(name, new BufferAttribute(values, size));
  });
  joined.setIndex(new BufferAttribute(joinedIndex(parts, firsts), 1));
  return joined;
}

function joinedIndex(parts: readonly BufferGeometry[], firsts: readonly number[]): Uint32Array {
  const indices = parts.map((part) => part.getIndex()?.array ?? new Uint32Array(0));
  const index = new Uint32Array(indices.reduce((sum, own) => sum + own.length, 0));
  indices.reduce((at, own, i) => {
    const first = firsts[i]!;
    for (let k = 0; k < own.length; k++) index[at + k] = own[k]! + first;
    return at + own.length;
  }, 0);
  return index;
}
